import {
    useContext,
    useEffect,
    useState
} from 'react';
import {
    Button,
    Col,
    Flex,
    Input,
    Modal,
    Row,
    Segmented,
    Select
} from 'antd';
import {
    v4 as uuidv4
} from 'uuid';
import {
    Error,
    Fieldset,
    Legend
} from '../styled-components/form';
import {
    Controller,
    SubmitHandler,
    useForm
} from 'react-hook-form';
import {
    SegmentEnum
} from '../constants';
import {
    languages
} from '../utils/languages';
import {
    useLazyQuery,
    useMutation
} from '@apollo/client';
import {
    NotificationContext
} from '../context/notification-provider';
import {
    RETRIEVE_TYPE_PRICE_BY_ID
} from '../apollo/query/prices.query';
import {
    INSERT_TYPE_PRICE,
    UPDATE_TYPE_PRICE
} from '../apollo/mutation/prices.mutation';
import TextArea from 'antd/es/input/TextArea';

export interface PropTypes {
    id: number | null;
    visible: number | null;
    setVisible: (state: null | number) => void;

}
const PriceTypeDetail = (props: PropTypes) => {
    /** Retrieve component properties */
    const { id, visible, setVisible } = props;
    /** State to manage the tab selected */
    const [tab, setTab] = useState<string>(SegmentEnum.GeneralInformation);
    /** State to manage the displayment of the translations */
    const [ translation, setTranslation] = useState<boolean>(false);
    /** Notification utilities */
    const { showNotification } = useContext(NotificationContext);
    /** Formulary configuration */
    const { control, handleSubmit, formState, reset } = useForm({
        mode: 'onChange',
        defaultValues: {
            sNameKey: uuidv4(),
            ...languages.reduce((acc: any, lang: { value: string }) => {
                acc[lang.value] = '';

                return acc;
            }, {})
        }
    });
    /** Manage to retrieve a category by id */
    const [ retrieveTypePriceById, { data } ] = useLazyQuery(RETRIEVE_TYPE_PRICE_BY_ID);
    /** Manage to insert a category */
    const [ insertTypePrice, { data: responseInsertion } ] = useMutation(INSERT_TYPE_PRICE, { refetchQueries: ['gqlRetrieveTypePrices'] });
    /** Manage to update a category */
    const [ updateTypePrice, { data: responseUpdation } ] = useMutation(UPDATE_TYPE_PRICE, { refetchQueries: ['gqlRetrieveTypePrices'] });
    
    /** Manage to retrieve the information of the category if we have an id */
    useEffect((): void => {
        reset({
            sNameKey: uuidv4(),
            ...languages.reduce((acc: any, lang: { value: string }) => {
                acc[lang.value] = '';

                return acc;
            }, {})
        });
        if ( id && id !== -1) {
            retrieveTypePriceById({ fetchPolicy: 'no-cache', variables: { nID: id } })
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);
    
    /** Manage to initialize the formulary with the category retrieved */
    useEffect((): void => {
        if( data?.data ) {
            const { nID, sNameKey, languages} = data?.data;

            reset({ nID, sNameKey, ...languages});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    /** Manage to close the category modal */
    const onClose = (): void => {
        setTab(SegmentEnum.GeneralInformation);
        reset();
        setVisible(null);
        setTranslation(false);
    }

    /** Manage to show the response alert when updation or insertion is done */
    useEffect((): void => {
        // eslint-disable-next-line eqeqeq
        if( responseInsertion?.code == 200 || responseUpdation?.code == 200 ) {
            showNotification({ severity: 'success', title: id && id !== -1 ? 'Tipus de preu actualitzat!' : 'Nou tipus de preu afegit!' });
            onClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [responseInsertion, responseUpdation]);
    
    /** Manage to request the insertion and updation of a type price */
    const onSubmit: SubmitHandler<any> = (data): void => {
        const { nID, sNameKey, ...languages } = data;
        if( id && id !== -1) {
            updateTypePrice({ variables: { price: { sNameKey, languages: languages }} });
        } else {
            insertTypePrice({ variables: { price: { sNameKey, languages: languages }} });
        }
    }
    
    return (
        <Modal
            title="Tipus de Preu"
            open={!!visible}
            onOk={handleSubmit(onSubmit)}
            okButtonProps={{ disabled: !formState.isValid }}
            onCancel={() => onClose()}
        >
            <Flex vertical>
                <Segmented
                    value={tab}
                    style={{ marginBottom: 8 }}
                    onChange={setTab}
                    options={[SegmentEnum.GeneralInformation]}
                />

                <form onSubmit={handleSubmit(onSubmit)}>
                    { tab === SegmentEnum.GeneralInformation && 
                        <Flex vertical align='flex-end'>
                            <Row style={{ width: '100%' }}>
                                <Col span={22}>
                                    <Controller
                                        name="ca"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field, fieldState }) => (
                                            <Fieldset $error={fieldState.invalid}>
                                                <Legend $error={fieldState.invalid}> Nom [Català] </Legend>
                                                <Input size="middle" style={{ height: '100%', width: '100%', border: 'none' }} {...field} />
                                                { fieldState.invalid && <Error> El camp és obligatori </Error> }
                                            </Fieldset>
                                        )}
                                    />
                                </Col>
                            </Row>
                                
                            { translation &&
                                <Flex vertical align='flex-end' gap={10} style={{ width: '100%', marginTop: 10 }}>
                                    { languages.map((language: any) => (
                                        <Row style={{ width: '90%', gap: 20 }} key={language.value}>
                                            <Col span={6}> <Select value={language.value} style={{ width: '100%' }} options={languages} disabled  /> </Col>
                                            <Col span={15}>
                                                <Controller
                                                    name={language.value}
                                                    control={control}
                                                    render={({ field }) => <TextArea autoSize {...field} style={{ minWidth: '100%' }} /> }
                                                />
                                            </Col>
                                        </Row>
                                    ))}
                                </Flex>
                            }
                            { !translation && <Button type='link' style={{ fontSize: '0.7rem', padding: 0, width: 'fit-content', textDecoration: 'underline' }} onClick={() => setTranslation(true)}> Afegir traduccions </Button> }
                            { translation && <Button type='link' style={{ fontSize: '0.7rem', padding: 0, width: 'fit-content', textDecoration: 'underline' }} onClick={() => setTranslation(false)}> Amagar traduccions </Button> }
                        </Flex>
                    }
                </form>
            </Flex>
        </Modal>
    );
}

export default PriceTypeDetail;